import { IPublicClientApplication, AccountInfo } from '@azure/msal-browser';
import { loginRequest } from '../auth/authConfig';

/**
 * Get a valid access token using silent refresh with fallback to redirect
 * @param {Object} instance - MSAL PublicClientApplication instance
 * @param {Array} accounts - Array of MSAL accounts
 * @returns {Promise<string>} Access token
 */
export async function getAccessToken(instance: IPublicClientApplication, accounts: AccountInfo[]): Promise<string> {
  if (!accounts || accounts.length === 0) {
    throw new Error('No active account found. Please sign in.');
  }

  const activeAccount = accounts[0];

  const request = {
    ...loginRequest,
    account: activeAccount,
  };

  try {
    // Try to acquire token silently (uses refresh token automatically)
    const response = await instance.acquireTokenSilent(request);
    return response.accessToken;
  } catch (error) {
    console.warn('Silent token acquisition failed:', error);

    // Check if it's an interaction required error
    if (error.name === 'InteractionRequiredAuthError') {
      try {
        // Fallback to interactive redirect flow
        await instance.acquireTokenRedirect(request);
        // Note: This will redirect the user, so code after this won't execute
      } catch (redirectError) {
        console.error('Token acquisition redirect failed:', redirectError);
        throw redirectError;
      }
    } else {
      // For other errors, throw them
      throw error;
    }
  }
}

/**
 * Get access token for API calls with automatic refresh
 * This is a convenience wrapper for getAccessToken
 * @param {Object} instance - MSAL PublicClientApplication instance
 * @param {Array} accounts - Array of MSAL accounts
 * @returns {Promise<string>} Access token
 */
export async function getTokenForApiCall(instance: IPublicClientApplication, accounts: AccountInfo[]): Promise<string> {
  return await getAccessToken(instance, accounts);
}
